import { getToolBySlug } from '@/lib/data/tools';

interface ToolPreviewCardProps {
  /** Bare tool slug (final URL segment), e.g. "threat-modeling-wizard" */
  slug: string;
}

/**
 * Compact preview card rendered in blog posts in place of an inline-embedded
 * wizard/assessment tool. Multi-step wizards are too tall and stateful to
 * inline alongside article text, so we show a small card that links out to
 * the canonical tool page instead.
 *
 * Renders server-side — no `'use client'` directive needed. If `getToolBySlug`
 * returns nothing (e.g. tool is registered in D1 only, not in `lib/data/tools.ts`),
 * we fall back to a minimal stub with the slug-derived link.
 *
 * Visual style mirrors the seoContent fallback in `MarkdownContent.tsx` for
 * consistency (rounded border, light bg, blue link).
 */
export default function ToolPreviewCard({ slug }: ToolPreviewCardProps) {
  const tool = getToolBySlug(slug);

  if (!tool) {
    const fallbackHref = `/tools/${slug}`;
    const fallbackName = slug
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
          {fallbackName}
        </h3>
        <a
          href={fallbackHref}
          className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          Open the {fallbackName} tool →
        </a>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800">
      <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
        {tool.name}
      </h3>
      <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
        {tool.description}
      </p>
      <a
        href={tool.link}
        className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
      >
        Open the full {tool.name} →
      </a>
    </div>
  );
}
